import { Button, Input, Select, Textarea } from "@medusajs/ui"
import { useState } from "react"
import { PageHeader, Section } from "@/components/ui"
import { Field } from "@/components/form"
import { Badge, Banner, DateRange, DefinitionList, FilterBar, FilterSelect, Stat, Tabs, type Tone } from "@/terabizz"

const TONES: Tone[] = ["neutral", "info", "success", "attention", "warning", "critical", "magic"]
const SURFACES: [string, string][] = [["canvas", "--tb-canvas"], ["surface", "--tb-surface"], ["surface-subtle", "--tb-surface-subtle"], ["line", "--tb-line"], ["ink", "--tb-ink"], ["ink-2", "--tb-ink-2"], ["ink-3", "--tb-ink-3"], ["ink-4", "--tb-ink-4"], ["primary", "--tb-primary"], ["link", "--tb-link"]]

export function TerabizzPage() {
  const [tab, setTab] = useState("all")
  const [f, setF] = useState({ a: "", from: "", to: "" })
  return (
    <>
      <PageHeader title="Terabizz" subtitle="The admin's component library, themed with the Keel identity: tokens and components used by every screen in Keel Admin." />
      <div className="flex flex-col gap-4">
        <Section title="Principles">
          <ol className="list-decimal space-y-1.5 px-8 py-3 text-[13px] text-[var(--tb-ink-2)]">
            <li><b>Calm canvas, bright surfaces.</b> A quiet grey canvas with white cards; nothing decorative.</li>
            <li><b>One strong action per view.</b> The dark primary button is the only heavy element.</li>
            <li><b>Density with air.</b> 13px text on a 4px grid, 44px table rows, generous card padding.</li>
            <li><b>Tactile controls.</b> Crisp 1px rings, a firmer bottom edge, instant 80ms feedback.</li>
            <li><b>Colour is meaning.</b> Tones appear on status only.</li>
            <li><b>Always reachable.</b> Visible focus rings, 40px+ touch targets on phones, reduced motion respected.</li>
          </ol>
        </Section>

        <Section title="Colour tokens">
          <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-5">
            {SURFACES.map(([n, v]) => (
              <div key={n} className="text-[12px]">
                <div className="h-10 rounded-[8px]" style={{ background: `var(${v})`, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.08)" }} />
                <div className="mt-1 font-medium">{n}</div>
                <code className="text-[var(--tb-ink-4)]">{v}</code>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Typography">
          <div className="space-y-2 p-4">
            <div className="text-[28px] font-semibold tracking-[-0.02em]">Display 28</div>
            <div className="text-[20px] font-semibold tracking-[-0.015em]">Page title 20</div>
            <div className="text-[14px] font-semibold">Card title 14</div>
            <div className="text-[13px]">Body 13 — the default size for everything in the admin.</div>
            <div className="text-[12px] text-[var(--tb-ink-3)]">Caption 12 — hints, table headers, metadata.</div>
          </div>
        </Section>

        <Section title="Status badges">
          <div className="flex flex-wrap gap-2 p-4">
            {TONES.map((t) => <Badge key={t} tone={t}>{t}</Badge>)}
            <Badge tone="attention" dot="ring">Unfulfilled</Badge>
            <Badge tone="neutral" dot="filled">Paid</Badge>
          </div>
        </Section>

        <div className="grid gap-4 sm:grid-cols-3">
          <Stat label="Total sales" value="$12,480.00" change={12} />
          <Stat label="Orders" value="312" change={-4} />
          <Stat label="Average order" value="$40.00" hint="Last 30 days" />
        </div>

        <Section title="Banners">
          <div className="flex flex-col gap-3 p-4">
            <Banner tone="info" title="Heads up">Your store password is on. Visitors see the password page.</Banner>
            <Banner tone="success">Gift card created.</Banner>
            <Banner tone="warning" title="3 products are low on stock" action={<span className="underline">Review inventory</span>} />
            <Banner tone="critical" title="Couldn't save">Check the highlighted fields and try again.</Banner>
          </div>
        </Section>

        <Section title="Buttons">
          <div className="flex flex-wrap items-center gap-2 p-4">
            <Button size="small">Save</Button>
            <Button size="small" variant="secondary">Export</Button>
            <Button size="small" variant="transparent">Cancel</Button>
            <Button size="small" variant="danger">Delete</Button>
            <Button size="small" disabled>Disabled</Button>
            <Button size="small" isLoading>Saving</Button>
          </div>
        </Section>

        <Section title="Fields">
          <div className="grid gap-4 p-4 sm:grid-cols-2">
            <Field label="Text field" hint="Helper text sits under the control."><Input placeholder="Short sleeve t-shirt" /></Field>
            <Field label="Select"><Select><Select.Trigger><Select.Value placeholder="Choose" /></Select.Trigger><Select.Content><Select.Item value="a">Option A</Select.Item><Select.Item value="b">Option B</Select.Item></Select.Content></Select></Field>
            <Field label="Date"><input type="date" /></Field>
            <Field label="Disabled"><Input disabled value="Read only" readOnly /></Field>
            <div className="sm:col-span-2"><Field label="Multi-line"><Textarea rows={3} placeholder="Write something" /></Field></div>
          </div>
        </Section>

        <Section title="Tabs and filters">
          <Tabs tabs={[{ id: "all", label: "All" }, { id: "open", label: "Open", count: 45 }, { id: "unpaid", label: "Unpaid", count: 26 }]} value={tab} onChange={setTab} />
          <FilterBar>
            <FilterSelect label="Payment" value={f.a} onChange={(a) => setF({ ...f, a })} options={[["paid", "Paid"], ["unpaid", "Pending"]]} />
            <DateRange from={f.from} to={f.to} onChange={(p) => setF({ ...f, ...p })} />
          </FilterBar>
        </Section>

        <Section title="Definition list">
          <DefinitionList rows={[["Subtotal", "$40.00"], ["Shipping", "$5.00"], ["Total", "$45.00", true]]} />
        </Section>
      </div>
    </>
  )
}
